import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { PortalShell } from '@/portal/components/PortalShell';
import { Ico } from '@/portal/components/Icons';
import { Drawer, statusPill } from '@/portal/components/ui';
import { Present } from '@/portal/components/Present';
import { AvailabilityBadge, ProductPeek, ProductImage, ProductTags } from '@/portal/components/ProductPeek';
import { SampleEnquiryModal } from '@/portal/components/SampleEnquiryModal';
import { ProductFilters, useProductFilters, applyProductFilters } from '@/portal/components/ProductFilters';
import { ShareModal } from '@/portal/components/ShareModal';
import { SharedLookbook, PrintLookbook } from '@/portal/components/Lookbook';
import { usePortalData } from '@/portal/data/PortalDataContext';
import { productPhotoUrl } from '@/portal/api/client';
import { useContent } from '@/services/siteData';
import { usePortalContact } from '@/portal/data/usePortalContact';
import { useAuth } from '@/portal/auth/AuthContext';
import type { Portfolio, PortalProduct, PortfolioDisplay } from '@/portal/api/types';

type Overlay = 'present' | 'share' | 'lookbook' | 'print' | 'sample-enquiry' | null;

export function PortfolioEditorPage() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const { portfolios, portfoliosLoading, productByCode, patchPortfolio, sharePortfolio } = usePortalData();
  const content = useContent();
  const didier = usePortalContact();
  const { user } = useAuth();
  const contact = {
    email: didier?.email || content.contact.email,
    phone: didier?.phone || content.contact.phone,
    phoneHref: didier?.phone ? `tel:${didier.phone.replace(/\s+/g, '')}` : content.contact.phoneHref,
  };

  const pf = portfolios.find((p) => p.id === id);

  const [title, setTitle] = useState('');
  const [client, setClient] = useState('');
  const [event, setEvent] = useState('');
  const [addOpen, setAddOpen] = useState(false);
  const [dispOpen, setDispOpen] = useState(false);
  const [overlay, setOverlay] = useState<Overlay>(null);

  useEffect(() => {
    if (pf) {
      setTitle(pf.title);
      setClient(pf.client ?? '');
      setEvent(pf.event ?? '');
    }
  }, [pf?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const products: PortalProduct[] = useMemo(
    () => (pf ? pf.item_codes.map((c) => productByCode.get(c)).filter((p): p is PortalProduct => !!p) : []),
    [pf, productByCode],
  );

  if (!pf) {
    return (
      <PortalShell title="Portfolio">
        <div className="pl-empty">{portfoliosLoading ? 'Loading…' : 'Portfolio not found.'}</div>
      </PortalShell>
    );
  }

  const saveMeta = (patch: Partial<{ title: string; client: string; event: string }>) => {
    void patchPortfolio(pf.id, patch);
  };

  const onShare = async () => {
    await sharePortfolio(pf.id);
    setOverlay('share');
  };

  const onEnquiry = () => {
    setOverlay('sample-enquiry');
  };

  const cover = pf.cover_code || pf.item_codes[0];
  const coverImg = cover ? productPhotoUrl(cover) : null;
  const meta = { title: pf.title, client: pf.client, event: pf.event, note: pf.note };

  return (
    <PortalShell title="Portfolio" crumb={pf.title}>
      <button className="pl-btn ghost sm" style={{ marginBottom: 16 }} onClick={() => navigate('/portal')}>
        <Ico.back width={16} height={16} /> Back to portfolios
      </button>

      <div className="pl-editor-head">
        <div className="pl-editor-cover" style={coverImg ? { backgroundImage: `url(${coverImg})` } : undefined} />
        <div className="pl-editor-meta">
          <input
            className="pl-title-input"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            onBlur={() => title.trim() && title !== pf.title && saveMeta({ title: title.trim() })}
            placeholder="Untitled portfolio"
          />
          <div style={{ display: 'flex', gap: 12, marginTop: 10, flexWrap: 'wrap', alignItems: 'center' }}>
            <input
              className="pl-inline-input"
              value={client}
              onChange={(e) => setClient(e.target.value)}
              onBlur={() => client !== (pf.client ?? '') && saveMeta({ client })}
              placeholder="Client"
            />
            <input
              className="pl-inline-input subtle"
              value={event}
              onChange={(e) => setEvent(e.target.value)}
              onBlur={() => event !== (pf.event ?? '') && saveMeta({ event })}
              placeholder="Event / occasion"
            />
            {statusPill(pf.status)}
            <span style={{ color: 'var(--ink-faint)', fontSize: 13 }}>{products.length} items</span>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 10, flex: 'none' }}>
          <button className="pl-btn" onClick={() => setDispOpen(true)}>
            <Ico.edit width={15} height={15} /> Display
          </button>
          <button className="pl-btn" onClick={onShare}>
            <Ico.share width={16} height={16} /> Share
          </button>
          <button className="pl-btn dark" onClick={() => setOverlay('present')} disabled={products.length === 0}>
            <Ico.play width={15} height={15} /> Present
          </button>
        </div>
      </div>

      <div className="pl-section-bar">
        <h3>Selection</h3>
        <button className="pl-btn primary sm" onClick={() => setAddOpen(true)}>
          <Ico.plus width={15} height={15} /> Add products
        </button>
      </div>

      {products.length === 0 ? (
        <div className="pl-empty">No products yet. Click “Add products” to build this presentation.</div>
      ) : (
        <SelectionBoard pf={pf} />
      )}

      {addOpen && <AddProductsDrawer pf={pf} onClose={() => setAddOpen(false)} />}
      {dispOpen && <DisplayDrawer pf={pf} onClose={() => setDispOpen(false)} />}

      {overlay === 'present' && (
        <Present meta={meta} products={products} display={pf.display} onClose={() => setOverlay(null)} />
      )}
      {overlay === 'share' && (
        <ShareModal
          portfolio={pf}
          onClose={() => setOverlay(null)}
          onOpenLookbook={() => setOverlay('lookbook')}
          onPrint={() => setOverlay('print')}
          onEnquiry={onEnquiry}
        />
      )}
      {overlay === 'lookbook' && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 300, overflowY: 'auto', background: 'var(--cream)' }}>
          <SharedLookbook
            meta={meta}
            products={products}
            display={pf.display}
            contact={contact}
            onClose={() => setOverlay(null)}
            onPrint={() => setOverlay('print')}
          />
        </div>
      )}
      {overlay === 'print' && (
        <PrintLookbook meta={meta} products={products} display={pf.display} contact={contact} onClose={() => setOverlay(null)} />
      )}
      {overlay === 'sample-enquiry' && (
        <SampleEnquiryModal
          portfolioTitle={pf.title}
          portfolioClient={pf.client}
          portfolioLink={pf.share_token ? `${location.origin}/shared/${pf.share_token}` : null}
          products={products}
          userName={user?.name || user?.username}
          userCompany={user?.company_name}
          onClose={() => setOverlay(null)}
        />
      )}
    </PortalShell>
  );
}

/* ---------- Selection board: grouped by category, drag-and-drop reorder ---------- */
interface CatGroup {
  key: string;
  label: string;
  items: PortalProduct[];
}

function SelectionBoard({ pf }: { pf: Portfolio }) {
  const { productByCode, setPortfolioItems } = usePortalData();
  const [codes, setCodes] = useState<string[]>(pf.item_codes);
  const [confirmCode, setConfirmCode] = useState<string | null>(null);
  const [dragCat, setDragCat] = useState<string | null>(null);
  const [overCat, setOverCat] = useState<string | null>(null);
  const [dragItem, setDragItem] = useState<{ cat: string; code: string } | null>(null);
  const [overCode, setOverCode] = useState<string | null>(null);

  // Keep the local working order in sync with the saved portfolio.
  useEffect(() => {
    setCodes(pf.item_codes);
  }, [pf.item_codes]);

  // Group by Category6 range; category order = order of first appearance in `codes`.
  const groups: CatGroup[] = useMemo(() => {
    const order: string[] = [];
    const map = new Map<string, PortalProduct[]>();
    codes.forEach((c) => {
      const p = productByCode.get(c);
      if (!p) return;
      if (!map.has(p.rangeKey)) {
        map.set(p.rangeKey, []);
        order.push(p.rangeKey);
      }
      map.get(p.rangeKey)!.push(p);
    });
    return order.map((key) => ({ key, label: map.get(key)![0].rangeLabel, items: map.get(key)! }));
  }, [codes, productByCode]);

  const persist = (next: string[]) => {
    setCodes(next);
    void setPortfolioItems(pf.id, next);
  };

  const removeItem = (code: string) => persist(codes.filter((c) => c !== code));

  const moveCategory = (from: string, to: string) => {
    if (from === to) return;
    const keys = groups.map((g) => g.key);
    const fi = keys.indexOf(from);
    const ti = keys.indexOf(to);
    if (fi < 0 || ti < 0) return;
    keys.splice(ti, 0, keys.splice(fi, 1)[0]);
    const byCat = new Map(groups.map((g) => [g.key, g.items.map((i) => i.code)]));
    persist(keys.flatMap((k) => byCat.get(k)!));
  };

  const moveItem = (cat: string, from: string, to: string) => {
    if (from === to) return;
    const next = groups.map((g) => {
      if (g.key !== cat) return g;
      const arr = [...g.items];
      const fi = arr.findIndex((i) => i.code === from);
      const ti = arr.findIndex((i) => i.code === to);
      if (fi < 0 || ti < 0) return g;
      arr.splice(ti, 0, arr.splice(fi, 1)[0]);
      return { ...g, items: arr };
    });
    persist(next.flatMap((g) => g.items.map((i) => i.code)));
  };

  return (
    <div className="pl-cats">
      {groups.map((g) => (
        <section
          key={g.key}
          className={'pl-cat' + (dragCat && overCat === g.key && overCat !== dragCat ? ' drop' : '')}
          onDragOver={(e) => {
            if (dragCat) {
              e.preventDefault();
              setOverCat(g.key);
            }
          }}
          onDrop={(e) => {
            if (dragCat) {
              e.preventDefault();
              moveCategory(dragCat, g.key);
              setDragCat(null);
              setOverCat(null);
            }
          }}
        >
          <div
            className="pl-cat-head"
            draggable
            onDragStart={(e) => {
              setDragCat(g.key);
              e.dataTransfer.effectAllowed = 'move';
            }}
            onDragEnd={() => {
              setDragCat(null);
              setOverCat(null);
            }}
            title="Drag to reorder this category"
          >
            <Ico.drag width={16} height={16} />
            <h4>{g.label}</h4>
            <span className="pl-cat-count">{g.items.length}</span>
          </div>

          <div className="pl-cat-grid">
            {g.items.map((p) => (
              <div
                key={p.id}
                className={
                  'pl-sel pl-sel-drag' +
                  (dragItem?.code === p.code ? ' dragging' : '') +
                  (dragItem && dragItem.cat === g.key && overCode === p.code && overCode !== dragItem.code
                    ? ' drop'
                    : '')
                }
                draggable
                onDragStart={(e) => {
                  e.stopPropagation();
                  setDragItem({ cat: g.key, code: p.code });
                  e.dataTransfer.effectAllowed = 'move';
                }}
                onDragEnd={() => {
                  setDragItem(null);
                  setOverCode(null);
                }}
                onDragOver={(e) => {
                  if (dragItem && dragItem.cat === g.key) {
                    e.preventDefault();
                    e.stopPropagation();
                    setOverCode(p.code);
                  }
                }}
                onDrop={(e) => {
                  if (dragItem && dragItem.cat === g.key) {
                    e.preventDefault();
                    e.stopPropagation();
                    moveItem(g.key, dragItem.code, p.code);
                    setDragItem(null);
                    setOverCode(null);
                  }
                }}
              >
                <div className="pl-sel-grip" title="Drag to reorder">
                  <Ico.drag width={15} height={15} />
                </div>
                <div className="pl-sel-img">
                  <ProductImage src={p.img} alt={p.name} draggable={false} />
                </div>
                <div className="pl-sel-body">
                  <div className="nm">{p.name}</div>
                  <span className="pl-code">{p.code}</span>
                  <AvailabilityBadge availability={p.availability} />
                </div>
                <div className="pl-sel-foot">
                  {confirmCode === p.code ? (
                    <div className="pl-confirm">
                      <button
                        className="pl-btn danger solid sm"
                        onClick={() => {
                          removeItem(p.code);
                          setConfirmCode(null);
                        }}
                      >
                        <Ico.check width={14} height={14} /> Confirm
                      </button>
                      <button className="pl-btn ghost sm" onClick={() => setConfirmCode(null)}>
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <button
                      className="pl-btn ghost sm danger pl-rm-btn"
                      onClick={() => setConfirmCode(p.code)}
                      title="Remove from this portfolio"
                    >
                      <Ico.x width={14} height={14} /> Remove
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}

/* ---------- Add products drawer ---------- */
function AddProductsDrawer({ pf, onClose }: { pf: Portfolio; onClose: () => void }) {
  const { collection, setPortfolioItems } = usePortalData();
  const filters = useProductFilters();
  const [peek, setPeek] = useState<PortalProduct | null>(null);

  const visible = useMemo(() => applyProductFilters(collection, filters), [collection, filters]);

  const toggle = (code: string) => {
    const has = pf.item_codes.includes(code);
    const next = has ? pf.item_codes.filter((c) => c !== code) : [...pf.item_codes, code];
    void setPortfolioItems(pf.id, next);
  };

  return (
    <Drawer
      title="Add products"
      onClose={onClose}
      stickyBar={
        <div className="pl-search" style={{ width: '100%' }}>
          <Ico.search width={16} height={16} />
          <input
            placeholder="Search your collection…"
            value={filters.q}
            onChange={(e) => filters.setQ(e.target.value)}
          />
        </div>
      }
      footer={
        <button className="pl-btn primary" onClick={onClose}>
          Done · {pf.item_codes.length} selected
        </button>
      }
    >
      <ProductFilters state={filters} source={collection} matchCount={visible.length} />

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        {visible.map((p) => {
          const added = pf.item_codes.includes(p.code);
          return (
            <div className="pl-prod" key={p.id}>
              <div
                className="pl-prod-tap"
                role="button"
                tabIndex={0}
                onClick={() => setPeek(p)}
                onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && setPeek(p)}
                aria-label={`View ${p.name}`}
              >
                <div className="pl-prod-img">
                  <ProductImage src={p.img} alt={p.name} loading="lazy" />
                </div>
                <div className="pl-prod-body">
                  <div className="nm">{p.name}</div>
                  <AvailabilityBadge availability={p.availability} />
                  <ProductTags allergens={p.allergens} suitability={p.suitability} />
                </div>
              </div>
              <div className="pl-prod-foot">
                {added ? (
                  <>
                    <span className="pl-added" title="In this portfolio">
                      <Ico.check width={15} height={15} /> Added
                    </span>
                    <button className="pl-btn ghost sm" onClick={() => toggle(p.code)} title="Remove from selection">
                      <Ico.x width={14} height={14} /> Remove
                    </button>
                  </>
                ) : (
                  <button
                    className="pl-btn primary sm pl-add-btn"
                    onClick={() => toggle(p.code)}
                    title="Add to selection"
                  >
                    <Ico.plus width={15} height={15} /> Add
                  </button>
                )}
              </div>
            </div>
          );
        })}
        {visible.length === 0 && (
          <div className="pl-empty" style={{ gridColumn: '1 / -1', padding: 30 }}>
            No products match these filters.
          </div>
        )}
      </div>

      {peek && (
        <ProductPeek
          product={peek}
          added={pf.item_codes.includes(peek.code)}
          onToggle={() => toggle(peek.code)}
          onClose={() => setPeek(null)}
        />
      )}
    </Drawer>
  );
}

/* ---------- Display options drawer ---------- */
function DisplayDrawer({ pf, onClose }: { pf: Portfolio; onClose: () => void }) {
  const { patchPortfolio } = usePortalData();
  const d = pf.display || {};
  const [note, setNote] = useState(pf.note ?? '');
  const always = ['Product name', 'Product code', 'Allergens'];
  const optional: Array<[keyof PortfolioDisplay, string]> = [
    ['category', 'Category'],
    ['packSize', 'Case size'],
    ['weight', 'Weight'],
    ['ingredients', 'Ingredient list'],
  ];
  // Some flags default ON (so existing portfolios keep their prior look).
  const defaults: Partial<Record<keyof PortfolioDisplay, boolean>> = { category: true };
  const isOn = (k: keyof PortfolioDisplay) => d[k] ?? defaults[k] ?? false;
  const toggle = (k: keyof PortfolioDisplay) => {
    void patchPortfolio(pf.id, { display: { ...d, [k]: !isOn(k) } });
  };

  return (
    <Drawer
      title="Presentation display"
      onClose={onClose}
      footer={
        <button className="pl-btn primary" onClick={onClose}>
          Done
        </button>
      }
    >
      <div className="pl-field">
        <label>Cover note (shown on the title slide)</label>
        <textarea
          rows={3}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          onBlur={() => note !== (pf.note ?? '') && patchPortfolio(pf.id, { note })}
          placeholder="A short introduction for this selection…"
        />
      </div>

      <p style={{ fontSize: 13.5, color: 'var(--ink-soft)', marginTop: 8 }}>
        Choose what appears on each product slide when you present or export this portfolio.
      </p>
      <div className="pl-filter-label" style={{ marginTop: 18 }}>
        Always shown
      </div>
      {always.map((a) => (
        <div className="pl-toggle-row" key={a}>
          <span style={{ color: 'var(--ink)', fontSize: 14 }}>{a}</span>
          <span style={{ color: 'var(--purple)', display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12.5 }}>
            <Ico.check width={15} height={15} /> On
          </span>
        </div>
      ))}
      <div className="pl-filter-label" style={{ marginTop: 18 }}>
        Optional
      </div>
      {optional.map(([k, label]) => (
        <div className="pl-toggle-row" key={k}>
          <span style={{ color: 'var(--ink)', fontSize: 14 }}>{label}</span>
          <button className={'pl-switch' + (isOn(k) ? ' on' : '')} onClick={() => toggle(k)} aria-label={label} />
        </div>
      ))}
    </Drawer>
  );
}
